import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import HeroSection from "../../components/feature/sections/HeroSection";
import TrustBar from "../../components/feature/sections/TrustBar";
import ServicesSection from "../../components/feature/sections/ServicesSection";
import AIAnswerBlock from "../../components/feature/sections/AIAnswerBlock";
import ProcessSection from "../../components/feature/sections/ProcessSection";
import WhyChooseUs from "../../components/feature/sections/WhyChooseUs";
import ServiceAreas from "../../components/feature/sections/ServiceAreas";
import TestimonialsSection from "../../components/feature/sections/TestimonialsSection";
import FAQSection from "../../components/feature/sections/FAQSection";
import FinalCTA from "../../components/feature/sections/FinalCTA";
import {
  COMPANY_INFO,
  SERVICES_DATA,
  PROCESS_STEPS_DATA,
  WHY_CHOOSE_DATA,
  FAQ_HOME_DATA,
  PROJECTS_DATA,
} from "../../mocks/aegis-data";

export default function Home() {
  return (
    <div className="font-inter bg-white">
      <Navbar />

      {/* 1. Hero */}
      <HeroSection
        badge="Sarasota's Premier Construction Partner"
        headline="Custom Home Builder & Luxury Remodeler in Sarasota, Florida"
        subheadline="In-house structural crews. Zero subcontractors on critical work. Florida-licensed general contractor serving Sarasota, Manatee & Charlotte County since 2011."
        ctaPrimary={{ text: "Get a Free Estimate", href: "#contact" }}
        ctaSecondary={{ text: "(941) 487-0007", href: "tel:9414870007" }}
      />

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Services */}
      <div id="services">
        <ServicesSection
          title="Complete Design-Build Construction Services"
          subtitle="From custom homes to structural renovations — all structural work self-performed by our in-house crews. No subcontractors on your critical path."
          services={SERVICES_DATA}
        />
      </div>

      {/* Mid-page CTA */}
      <div className="bg-aegisDark py-16 relative overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-aegisGold/40 to-transparent" />
        <div className="max-w-7xl mx-auto px-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <span className="font-inter text-xs tracking-widest uppercase text-aegisGold block mb-3">
              No Subcontractors. No Delays. No Compromises.
            </span>
            <h3 className="font-cormorant font-semibold text-white text-3xl md:text-4xl leading-tight max-w-xl">
              Your Sarasota Build Deserves One Team, One Standard.
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href={COMPANY_INFO.phoneHref}
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-inter font-medium text-sm tracking-wide uppercase px-7 py-3.5 whitespace-nowrap cursor-pointer hover:border-aegisGold hover:text-aegisGold transition-all duration-200"
            >
              <i className="ri-phone-line" />
              (941) 487-0007
            </a>
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 bg-aegisGold text-white font-inter font-semibold text-sm tracking-widest uppercase px-7 py-3.5 whitespace-nowrap cursor-pointer hover:bg-amber-700 transition-colors duration-200"
            >
              Start Your Project
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>

      {/* 4. AI Answer Block */}
      <AIAnswerBlock
        description="Aegis Construction Services LLC is a Florida-licensed general contractor (CGC1524141) based in Sarasota, specializing in custom home building and high-end residential remodeling. Founded in 2011 by owner Armando Oyola-Delgado — a construction professional since 1998 — Aegis builds and renovates homes throughout Sarasota, Manatee, and Charlotte County with in-house crews and zero subcontracting on structural work."
        services={SERVICES_DATA.map((s) => s.title)}
        approach="We self-perform all structural work — foundations, masonry, framing, and plumbing — with our own in-house crews. One team, one standard, one point of accountability from land to final walkthrough. No subcontractors on your critical path."
        benefits={[
          "In-house crews on all structural work — no subcontractors",
          "Dual-licensed: CGC1524141 (GC) + CFC1430823 (Plumbing)",
          "Custom homes from 4,000+ sq ft on the Gulf Coast",
          "Over 1,000 homes built across Florida",
          "Coastal & flood zone specialists, barrier island experience",
        ]}
      />

      {/* 5. Process */}
      <div id="process">
        <ProcessSection
          title="How We Build"
          subtitle="From first consultation to final walkthrough — one team, one standard, every phase."
          steps={PROCESS_STEPS_DATA}
        />
      </div>

      {/* 6. Why Choose Us */}
      <div id="why-us">
        <WhyChooseUs
          title="Why Sarasota Homeowners Choose Aegis"
          subtitle="What separates Aegis from most local contractors is simple — we do the work ourselves. No subcontractors on your foundations, masonry, framing, or plumbing."
          benefits={WHY_CHOOSE_DATA}
          stats={[
            { value: "2011", label: "Founded" },
            { value: "1,000+", label: "Homes Built" },
            { value: "25+", label: "Years Experience" },
          ]}
        />
      </div>

      {/* 7. Service Areas */}
      <ServiceAreas />

      {/* 8. Testimonials */}
      <TestimonialsSection />

      {/* Latest Projects */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-inter text-xs tracking-widest uppercase text-aegisGold">
              Recent Work
            </span>
            <h2 className="font-cormorant font-semibold text-aegisDark text-4xl md:text-5xl mt-3 mb-5">
              Latest Projects
            </h2>
            <div className="w-12 h-0.5 bg-aegisGold mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PROJECTS_DATA.map((p) => (
              <div key={p.id} className="group border border-aegisStone overflow-hidden hover:border-aegisGold transition-colors duration-300">
                <div className="overflow-hidden h-52 w-full">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="font-inter text-xs tracking-widest uppercase text-aegisGold">
                    {p.subtitle}
                  </span>
                  <h3 className="font-cormorant font-semibold text-aegisDark text-xl mt-1 mb-2">
                    {p.title}
                  </h3>
                  <p className="font-inter text-aegisMuted text-sm leading-relaxed">
                    {p.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <FAQSection
        title="Frequently Asked Questions"
        subtitle="Voice-search ready answers for Sarasota homeowners considering custom home building or luxury remodeling."
        faqs={FAQ_HOME_DATA}
      />

      {/* 10. Final CTA */}
      <div id="contact">
        <FinalCTA
          title="Start Your Sarasota Custom Home or Remodel Today"
          subtitle="Get real project numbers in minutes. No obligation, no phone call required. We respond within 24 hours. Licensed CGC1524141 · Serving Sarasota, Manatee & Charlotte County."
          ctaLabel="Get a Free Estimate"
        />
      </div>

      <Footer />
    </div>
  );
}